import { useEffect, useMemo, useState } from 'react';

import { TagModel } from 'models/TagModel';

import { NotificationsService } from 'core/notifications';

import { useApiClient } from 'features/api';
import { AuthenticationScheme, useAuthentication } from 'features/auth';

// Helper to flatten tags
const flattenTags = (tags: TagModel[]): TagModel[] => {
  return tags.reduce((acc: TagModel[], tag) => {
    // Create a shallow copy without children for the flat list
    const { children, ...tagWithoutChildren } = tag;
    acc.push(tagWithoutChildren);
    if (children) {
      acc.push(...flattenTags(children));
    }
    return acc;
  }, []);
};

interface UseTagLookupsResult {
  allHierarchicalTags: TagModel[];
  allFlatTags: TagModel[];
  tagNameToIdMap: Map<string, number>;
  tagIdToTagMap: Map<number, TagModel>;
  isLoadingTags: boolean;
}

export const useTagLookups = (): UseTagLookupsResult => {
  const apiClient = useApiClient();
  const { activeScheme } = useAuthentication();
  const [allHierarchicalTags, setAllHierarchicalTags] = useState<TagModel[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState<boolean>(true);

  useEffect(() => {
    const fetchTags = async () => {
      setIsLoadingTags(true);

      // Don't make API calls if authentication is not initialized
      if (activeScheme === undefined) {
        console.log('[useTagLookups] Skipping tag fetch - authentication not initialized');
        setIsLoadingTags(false);
        return;
      }

      try {
        // Fetch hierarchical tags (includes children) - auth mode auto-detected by HttpClient
        const tags = await apiClient.tagsClient.getLevelTags();
        setAllHierarchicalTags(tags);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        NotificationsService.displayError('Could not load tags for lookups.');
        setAllHierarchicalTags([]);
      } finally {
        setIsLoadingTags(false);
      }
    };

    fetchTags();
  }, [apiClient, activeScheme]);

  // Memoize the flattened list and maps based on the hierarchical tags
  const allFlatTags = useMemo(() => flattenTags(allHierarchicalTags), [allHierarchicalTags]);

  const tagIdToTagMap = useMemo(() => {
    const idMap = new Map<number, TagModel>();
    allFlatTags.forEach((tag) => {
      idMap.set(tag.id, tag);
    });
    return idMap;
  }, [allFlatTags]);

  const tagNameToIdMap = useMemo(() => {
    const nameMap = new Map<string, number>();
    allFlatTags.forEach((tag) => {
      nameMap.set(tag.name.toLowerCase(), tag.id); // Use lowercase for robust matching
    });
    return nameMap;
  }, [allFlatTags]);

  return {
    allHierarchicalTags,
    allFlatTags,
    tagNameToIdMap,
    tagIdToTagMap,
    isLoadingTags,
  };
};
